import { Checkbox } from 'components/FormUtils/Checkbox'
import { TextInput } from 'components/FormUtils/TextInput'
import { Fragment, useState, useEffect } from 'react'
import { AiOutlineWarning } from 'react-icons/ai'
import { ChevronDown, ChevronUp } from 'lucide-react'

import { formatInteger, formatUsdDecimal } from '../../../utils/formatting'
import { NumericInput } from '../../FormUtils/NumericInput'
import Select from '../../FormUtils/Select'
import { cn } from '../../UI/cn'
import { DealsFormValues } from './CreateDealModal'
import { Control, useFormState, useWatch, FieldPath } from 'react-hook-form'
import useApiService from 'services/api'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { CompanyTypeSlug } from 'models/companies'
import { SingleVariantPicker } from 'components/UI/SingleVariantPicker'
import type { SelectedVariant } from 'components/UI/ProductSearchModal'
import { isNonRecurringProduct } from 'models/product'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { getSingularUnit } from 'utils/reportingUnits'

export default function DealDetailsStep({
  predictions,
  control,
  setValue,
  companyTypeSlug,
  existingDealsByProduct,
  originCampaignId,
  showDealName = true,
  companyDetails,
}: {
  predictions?: any[]
  control: Control<DealsFormValues>
  setValue: any
  companyTypeSlug?: CompanyTypeSlug
  existingDealsByProduct?: Record<number, number>
  originCampaignId?: number
  showDealName?: boolean
  companyDetails?: any
}) {
  const api = useApiService()
  const featureFlag = useFeatureFlagService()
  const { preferences } = usePreferences()
  const { data: stageOptions } = api.useGetSalesStageOptions()
  const { data: userOptions } = api.useGetUserOptions({ onlyActive: true })

  // Filter campaigns by the contact company's sub_category slug if available
  // This ensures only campaigns matching the opportunity type are shown
  const campaignTypeFilter =
    companyDetails?.sub_category?.slug ?? companyTypeSlug
  const { data: campaignOptions } = api.useGetCampaignsOptions({
    typeOrTypeId: campaignTypeFilter,
    requireFilter: true, // Prevent selecting campaigns when subcategory is unknown
  })

  const [overridesExpanded, setOverridesExpanded] = useState(false)
  const [selectedVariants, setSelectedVariants] = useState<
    Map<number, SelectedVariant>
  >(new Map())

  const watch = useWatch({ control })
  const { errors } = useFormState({ control })
  const { data: products } = api.useGetProducts()

  // Initialize selectedVariants from existing form values
  useEffect(() => {
    if (!watch?.deals || !products) return

    const newSelectedVariants = new Map<number, SelectedVariant>()

    watch.deals.forEach((deal, index) => {
      const variantId = deal.product_variant
      if (variantId && predictions?.[index]) {
        // Find the product for this deal
        const productId = predictions[index].product?.id
        const product = products.find((p) => p.id === productId)

        if (product?.variants) {
          // Find the variant details
          const variant = product.variants.find((v) => v.id === variantId)
          if (variant) {
            newSelectedVariants.set(index, {
              id: variant.id!,
              name: variant.name,
              format_pack_size: variant.format_pack_size || null,
              sku: variant.sku || '',
              gtin: variant.gtin || null,
              product_id: product.id,
              product_name: product.name,
              distributor_codes: variant.distributor_codes || [],
              avg_price: variant.avg_price ?? null,
            })
          }
        }
      }
    })

    setSelectedVariants(newSelectedVariants)
  }, [watch?.deals, products, predictions])

  return (
    <Fragment>
      <div className="gap-4">
        {predictions?.map((item, index) => {
          const active = watch?.deals?.[index]?.create
          const stage = watch?.deals?.[index]?.sales_stage
          const avgDaysToClose = stageOptions?.find((it) => it.value === stage)
            ?.original?.avg_days_to_close

          const date = new Date()
          if (avgDaysToClose) {
            date.setDate(date.getDate() + avgDaysToClose)
          }

          const existingDeals =
            existingDealsByProduct?.[item.product?.id ?? -1] ?? 0

          const isNonRecurring = isNonRecurringProduct(item.product)
          const reportingUnit = preferences?.reporting_unit || 'lbs'
          const singularUnit = getSingularUnit(reportingUnit)

          // Watch form values for calculated revenue
          const volumeOverride = watch?.deals?.[index]?.monthly_volume_override
          const priceOverride = watch?.deals?.[index]?.monthly_revenue_override
          const calculatedRevenue =
            isNonRecurring && volumeOverride != null && priceOverride != null
              ? volumeOverride * priceOverride
              : null

          return (
            <div
              key={index}
              className="w-full bg-white rounded-lg shadow-md p-3 border border-gray-200 mb-3"
            >
              <div
                className={cn(
                  'flex flex-row gap-2 items-center cursor-pointer',
                  {
                    'mb-4': active,
                  }
                )}
                onClick={() => {
                  const currentValue = watch?.deals?.[index]?.create ?? false
                  setValue(`deals.${index}.create`, !currentValue)
                }}
              >
                <div onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    control={control}
                    name={`deals.${index}.create`}
                    wrapperStyle={{ width: 'unset' }}
                  />
                </div>
                <h3 className="text-lg text-gray-800 flex-1 flex items-center gap-4">
                  <span>
                    Product: <b>{item.product.name}</b>
                  </span>
                  {isNonRecurring && (
                    <span className="text-sm text-gray-500 font-normal">
                      non recurring revenue
                    </span>
                  )}
                </h3>
              </div>

              <div
                className={cn({
                  hidden: !active,
                })}
              >
                {/* Deal Name Field */}
                {active && showDealName && (
                  <div className="mb-4">
                    <TextInput
                      control={control}
                      name={`deals.${index}.name`}
                      label="Deal Name"
                      placeholder={
                        companyDetails?.name ||
                        'Enter custom deal name (optional)'
                      }
                      optional={true}
                      disabled={!active}
                      maxLength={100}
                    />
                  </div>
                )}

                {/* Product Variant Selection */}
                {active && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Product Variant
                      <span className="text-gray-500 ml-1">(Optional)</span>
                    </label>
                    <SingleVariantPicker
                      productId={item.product?.id || null}
                      selectedVariant={selectedVariants.get(index) || null}
                      onVariantSelect={(variant) => {
                        const fieldName =
                          `deals.${index}.product_variant` as FieldPath<DealsFormValues>

                        if (variant) {
                          // Set the variant in form and local state
                          setValue(fieldName, variant.id, {
                            shouldValidate: true,
                          })
                          setSelectedVariants((prev) => {
                            const newMap = new Map(prev)
                            newMap.set(index, variant)
                            return newMap
                          })
                        } else {
                          // Clear the variant
                          setValue(fieldName, null, { shouldValidate: true })
                          setSelectedVariants((prev) => {
                            const newMap = new Map(prev)
                            newMap.delete(index)
                            return newMap
                          })
                        }
                      }}
                      buttonText="Select Variant"
                      disabled={!active}
                    />
                  </div>
                )}

                <div
                  className={cn(
                    'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-5 '
                  )}
                >
                  <div>
                    <Select
                      options={stageOptions}
                      control={control}
                      name={`deals.${index}.sales_stage`}
                      label={'Sales Stage'}
                      disabled={!active}
                    />
                    <span className="text-sm text-gray-600 mt-2">
                      {stage ? (
                        <Fragment>
                          <b>Estimated Close Date:</b>{' '}
                          {date.toLocaleDateString('en-US')}
                        </Fragment>
                      ) : (
                        ' '
                      )}
                    </span>
                  </div>
                  <div>
                    <Select
                      options={userOptions}
                      control={control}
                      name={`deals.${index}.account_owner`}
                      label="Account Owner"
                      disabled={!active}
                    />
                  </div>
                  <div>
                    {featureFlag.enableCampaigns ? (
                      <Select
                        options={campaignOptions}
                        control={control}
                        name={`deals.${index}.origin_campaign_id`}
                        label="Origin Campaign"
                        disabled={!active || originCampaignId != undefined}
                      />
                    ) : null}
                  </div>
                </div>

                {/* Deal Overrides Section */}
                {active && (
                  <div className="mt-6">
                    <div className="bg-surface-muted rounded-lg p-4">
                      <button
                        type="button"
                        onClick={() => setOverridesExpanded(!overridesExpanded)}
                        className="w-full flex items-center justify-between text-left"
                      >
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="text-sm font-medium text-gray-900">
                            Deal Overrides
                          </h4>
                          <span className="bg-sage-lt text-gray-500 text-xs px-2 py-1 rounded-full">
                            Optional
                          </span>
                          {calculatedRevenue != null && (
                            <span className="text-sm text-gray-700">
                              Est. Revenue:{' '}
                              <b>{formatUsdDecimal(calculatedRevenue)}</b>
                            </span>
                          )}
                        </div>
                        {overridesExpanded ? (
                          <ChevronUp className="h-5 w-5 text-gray-500" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-gray-500" />
                        )}
                      </button>

                      {overridesExpanded && (
                        <div className="mt-4 space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <NumericInput
                                control={control}
                                fieldName={`deals.${index}.monthly_volume_override`}
                                label={
                                  isNonRecurring
                                    ? 'Est. Volume Override'
                                    : 'Est. Monthly Volume'
                                }
                                optional={true}
                                disabled={!active}
                              />
                              {item.predicted_volume ? (
                                <span className="text-sm text-gray-600 mt-1 block">
                                  <b>
                                    {isNonRecurring
                                      ? 'Predicted Volume:'
                                      : 'Predicted Monthly:'}
                                  </b>{' '}
                                  {formatInteger(item.predicted_volume ?? 0)}
                                  {isNonRecurring ? ` ${singularUnit}(s)` : ''}
                                </span>
                              ) : null}
                            </div>
                            <div>
                              <NumericInput
                                control={control}
                                fieldName={`deals.${index}.monthly_revenue_override`}
                                label={
                                  isNonRecurring
                                    ? 'Est. Price Override'
                                    : 'Est. Monthly Revenue'
                                }
                                optional={true}
                                disabled={!active}
                                decimalScale={2}
                              />
                              {isNonRecurring && item.predicted_price ? (
                                <span className="text-sm text-gray-600 mt-1 block">
                                  <b>Avg. Price:</b>{' '}
                                  {formatUsdDecimal(item.predicted_price)}
                                </span>
                              ) : item.predicted_revenue ? (
                                <span className="text-sm text-gray-600 mt-1 block">
                                  <b>Predicted Monthly:</b>{' '}
                                  {formatUsdDecimal(item.predicted_revenue)}
                                </span>
                              ) : null}
                            </div>
                            <TextInput
                              label="Est. Close Date"
                              control={control}
                              name={`deals.${index}.close_date_override`}
                              type="date"
                              optional={true}
                              disabled={!active}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/*// @ts-expect-error ignore */}
                {errors?.['custom']?.message && (
                  <div className="text-red-500 mt-4">
                    {/*// @ts-expect-error ignore */}
                    {errors.custom.message}
                  </div>
                )}
                {existingDeals ? (
                  <div className="pt-3 flex items-center gap-2">
                    <AiOutlineWarning size={14} color="#FCBD00" />
                    {existingDeals > 1 ? (
                      <span>
                        Some companies in your selection already have a deal for
                        this product.
                      </span>
                    ) : (
                      <span>
                        The company you selected already has a deal for this
                        product.
                      </span>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          )
        })}
      </div>
    </Fragment>
  )
}
